import BackToTopButton from '@atoms/BackToTopButton/BackToTopButton';
import { IFooterBannerProps } from '@models/IFooterBannerProps';
import VerticalDirectionEnum from '@models/enums/VerticalDirectionEnum';
import Dropdown from '@molecules/Dropdown/Dropdown';
import useCookieSettings from '@utils/hooks/useCookieSettings';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

const FooterBanner = ({
  backToTopButtonText,
  copyrightNotice,
  enableStickyBackToTopButton,
  externalSitesLinks,
  language,
  languageSelector,
  secondaryNavigationLinks,
  cookieSettingsLabel,
}: IFooterBannerProps) => {
  const [isLanguageDropdownSelectorOpen, setIsLanguageDropdownSelectorOpen] =
    useState(false);
  const [languageActiveIndex, setLanguageActiveIndex] = useState(-1);
  const [isExternalSitesDropdownOpen, setIsExternalSitesDropdownOpen] =
    useState(false);
  const isBelowLargeDesktop = useMediaQuery(1440);

  const cookieSettingsButtonRef = useRef<HTMLButtonElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const footerInView = useInView(footerRef, { amount: 0.5 });
  const isInIFrame = useIsInIFrame();

  const onCookieSettingsPopupClose = () => {
    cookieSettingsButtonRef.current?.focus();
  };
  const { isCookieSettingsAvailable, displayCookieSettings } =
    useCookieSettings(onCookieSettingsPopupClose);

  useEffect(
    () =>
      setLanguageActiveIndex(
        languageSelector?.links?.findIndex((lang) =>
          lang.url?.includes(language ?? '')
        ) ?? -1
      ),
    [language, languageSelector]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsLanguageDropdownSelectorOpen(false);
        setIsExternalSitesDropdownOpen(false);
      }
    };

    if (isLanguageDropdownSelectorOpen || isExternalSitesDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLanguageDropdownSelectorOpen, isExternalSitesDropdownOpen]);

  return (
    <>
      <div
        className="container flex min-h-[90px] flex-wrap items-center gap-x-5 gap-y-[10px] py-6 md:py-[20px]"
        ref={footerRef}
        data-testid="CT-02-Footer-Banner"
      >
        {languageSelector?.links && languageSelector?.title && (
          <>
            <Dropdown
              activeIndex={languageActiveIndex}
              animateUnderline
              isCountrySelector
              isOpen={isLanguageDropdownSelectorOpen}
              links={languageSelector.links}
              modalTitle={languageSelector.title}
              openFromDirection={VerticalDirectionEnum.Up}
              setIsOpen={setIsLanguageDropdownSelectorOpen}
              title={languageSelector.links[languageActiveIndex]?.shortName}
              titleCallback={() => {
                setIsLanguageDropdownSelectorOpen(
                  !isLanguageDropdownSelectorOpen
                );
                setIsExternalSitesDropdownOpen(false);
              }}
            />
            <div className="h-6 w-px bg-grey-500" />
          </>
        )}

        <Dropdown
          modalTitle={externalSitesLinks?.title ?? ''}
          openFromDirection={VerticalDirectionEnum.Up}
          links={externalSitesLinks?.links ?? []}
          title={externalSitesLinks?.title}
          isOpen={isExternalSitesDropdownOpen}
          setIsOpen={setIsExternalSitesDropdownOpen}
          titleCallback={() => {
            setIsExternalSitesDropdownOpen(!isExternalSitesDropdownOpen);
            setIsLanguageDropdownSelectorOpen(false);
          }}
        />

        <ul className="flex w-full flex-wrap justify-start md:w-auto md:justify-end xl:justify-start">
          {secondaryNavigationLinks?.map((link) => (
            <li
              className="footer-pipe shrink-0 px-2 first:pl-0"
              key={`second-nav-${link.name}-${link.url}`}
            >
              <a
                className="body-four text-grey-500 transition-colors hover:text-yellow"
                href={link.url}
                target={isInIFrame ? '_parent' : link.target}
                data-testid="CT-02-Secondary-Nav-Link"
              >
                {link.name}
              </a>
            </li>
          ))}

          {cookieSettingsLabel && isCookieSettingsAvailable && (
            <li className="footer-pipe shrink-0 px-2">
              <button
                ref={cookieSettingsButtonRef}
                onClick={displayCookieSettings}
                className="body-four cursor-pointer text-grey-500 transition-colors hover:text-yellow"
                data-testid="CT-02-Footer-Cookie-Settings"
              >
                {cookieSettingsLabel}
              </button>
            </li>
          )}
        </ul>

        <div className="mr-auto flex w-full items-center text-left md:text-right xl:w-auto">
          {copyrightNotice && (
            <p
              className="body-four text-grey-500"
              data-testid="CT-02-Footer-Trademark"
            >
              {copyrightNotice}
            </p>
          )}
        </div>

        {!isBelowLargeDesktop && !isInIFrame && (
          <div className="flex items-center text-left md:text-right xl:w-auto">
            <BackToTopButton
              footerInView={footerInView}
              backToTopButtonText={backToTopButtonText}
              enableStickyBackToTopButton={enableStickyBackToTopButton ?? false}
            />
          </div>
        )}
      </div>
      {isBelowLargeDesktop && !isInIFrame && (
        <div className="flex h-[88px] w-full items-center bg-grey-700 text-left">
          <BackToTopButton
            footerInView={footerInView}
            backToTopButtonText={backToTopButtonText}
            enableStickyBackToTopButton={enableStickyBackToTopButton ?? false}
          />
        </div>
      )}
    </>
  );
};

export default FooterBanner;
